import { useEffect, useMemo, useState, useContext } from "react";
import FundingChart, { type FundingChartRow, FundingFilter } from "./FundingChart";
import FundingTable from "../components/FundingTable";
import AssetValueChartFilter from "v2/features/plans/components/AssetValueChartFilter";
import { getSessionStore } from "app/utils/sessionStore";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { makeRequest } from "app/utils/makeRequest";
import { USERTYPE, ProposalTypes, planHasCreditType } from "app/utils/types";
import { getThemeColors } from "app/utils/utils";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { Accordion, AccordionTab } from "primereact/accordion";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";
import FundingLedger from "./FundingLedger";

// ── Types ─────────────────────────────────────────────────────────────────────

interface InsuredPerson {
  id: string;
  name: string;
}

type FundingRow = FundingChartRow;

interface ApiGraphItem {
  planYear: number;
  year: number;
  insuredAge: number;
  clientPremium: { actual: number; illustrated: number; trueIQIndex: number };
  lenderPremium: { actual: number; illustrated: number; trueIQIndex: number };
  interestPayment: { actual: number; illustrated: number; trueIQIndex: number };
  planFee: { actual: number; illustrated: number; trueIQIndex: number };
  originationFee: { illustrated: number };
  combinedTrueIQIndex: number;
  interestPaymentInvoiceStatus: string;
  lenderPremiumInvoiceStatus: string;
  planFeeInvoiceStatus: string;
}

interface ApiTableItem {
  planYear: number; // sequential (1, 2, 3…)
  year: number; // calendar year (2026)
  insuredAge: number;
  clientPremium: { actual: number; illustrated: number };
  lenderPremium: { actual: number; illustrated: number };
  interestPayment: { actual: number; illustrated: number };
  planFee: { actual: number; illustrated: number };
  originationFee: { actual: number; illustrated: number };
  total: { actual: number; illustrated: number };
}

// API planYear = sequential (1,2,3…), API year = calendar year (2026)
// FundingRow.year = sequential, FundingRow.planYear = calendar year
const mapToFundingRow = (item: ApiGraphItem): FundingRow => ({
  planYear: item.year,
  year: item.planYear,
  insuredAge: item.insuredAge,
  clientPremium: item.clientPremium.actual,
  clientPlanFee: item.planFee.actual,
  clientInterestPayment: item.interestPayment.actual,
  clientOriginationFees: item.originationFee?.illustrated ?? 0,
  clientOriginationFeesIllustrated: item.originationFee?.illustrated ?? 0,
  lenderPremium: item.lenderPremium.actual,
  lenderOriginationFees: 0,
  lenderOriginationFeesIllustrated: 0,
  totalPolicyPremium: item.clientPremium.actual + item.lenderPremium.actual,
  clientPremiumIllustrated: item.clientPremium.illustrated,
  lenderPremiumIllustrated: item.lenderPremium.illustrated,
  clientInterestPaymentIllustrated: item.interestPayment.illustrated,
  clientPlanFeeIllustrated: item.planFee.illustrated,
  combinedTrueIQIndex: item.combinedTrueIQIndex,
  interestPaymentInvoiceStatus: item.interestPaymentInvoiceStatus,
  lenderPremiumInvoiceStatus: item.lenderPremiumInvoiceStatus,
  planFeeInvoiceStatus: item.planFeeInvoiceStatus,
});

const mapTableItemToFundingRow = (item: ApiTableItem): any => ({
  planYear: item.year, // calendar year → "Plan Year" column
  year: item.planYear, // sequential    → "Year" column + row highlight
  insuredAge: item.insuredAge,
  clientPremium: item.clientPremium.actual,
  clientPlanFee: item.planFee.actual,
  clientInterestPayment: item.interestPayment.actual,
  clientOriginationFees: item.originationFee.actual,
  clientOriginationFeesIllustrated: item.originationFee.illustrated,
  lenderPremium: item.lenderPremium.actual,
  lenderOriginationFees: 0,
  lenderOriginationFeesIllustrated: 0,
  totalPolicyPremium: item.total.actual,
  clientPremiumIllustrated: item.clientPremium.illustrated,
  lenderPremiumIllustrated: item.lenderPremium.illustrated,
  clientInterestPaymentIllustrated: item.interestPayment.illustrated,
  clientPlanFeeIllustrated: item.planFee.illustrated,
});

// ── Key Person mappers ──────────────────────────────────────────────────────
// KP's funding API uses life/disability/planFee (graph) and
// annualLifePremium/annualDisabilityPremium/totalAnnualPremium/totalPlanCosts
// (table) instead of the PF client/lender/interest/origination fields. We map
// Life → clientPremium and Disability → lenderPremium so the existing chart /
// table machinery (and the Life/Disability/Plan Fees filters) work unchanged.
const num = (v: { actual?: number } | null | undefined) => v?.actual ?? 0;
const numIll = (v: { illustrated?: number } | null | undefined) => v?.illustrated ?? 0;

const mapKpGraphItemToFundingRow = (item: any): FundingRow => ({
  planYear: item.year,
  year: item.planYear,
  insuredAge: item.insuredAge,
  clientPremium: num(item.life),
  lenderPremium: num(item.disability),
  clientPlanFee: num(item.planFee),
  clientInterestPayment: 0,
  clientOriginationFees: 0,
  clientOriginationFeesIllustrated: 0,
  lenderOriginationFees: 0,
  lenderOriginationFeesIllustrated: 0,
  totalPolicyPremium: num(item.life) + num(item.disability) + num(item.planFee),
  clientPremiumIllustrated: numIll(item.life),
  lenderPremiumIllustrated: numIll(item.disability),
  clientInterestPaymentIllustrated: 0,
  clientPlanFeeIllustrated: numIll(item.planFee),
  combinedTrueIQIndex: item.combinedTrueIQIndex,
  planFeeInvoiceStatus: item.planFeeInvoiceStatus,
});

const mapKpTableItemToFundingRow = (item: any): any => ({
  planYear: item.year,
  year: item.planYear,
  insuredAge: item.insuredAge,
  clientPremium: num(item.annualLifePremium),
  clientPremiumIllustrated: numIll(item.annualLifePremium),
  lenderPremium: num(item.annualDisabilityPremium),
  lenderPremiumIllustrated: numIll(item.annualDisabilityPremium),
  clientPlanFee: num(item.planFee),
  clientPlanFeeIllustrated: numIll(item.planFee),
  totalAnnualPremium: num(item.totalAnnualPremium),
  totalPlanCosts: num(item.totalPlanCosts),
  totalPolicyPremium: num(item.totalPlanCosts),
  planFeeInvoiceStatus: item.planFeeInvoiceStatus,
});

// ── Filter config ─────────────────────────────────────────────────────────────

const PRIMARY = "#1E3A8A";

interface FilterOption {
  id: string;
  label: string;
  color: string;
  field: keyof Pick<
    FundingRow,
    | "clientPremium"
    | "clientPlanFee"
    | "clientInterestPayment"
    | "clientOriginationFees"
    | "lenderPremium"
  >;
}

const makeFilterOptions = (brandColor: string): FilterOption[] => [
  {
    id: FundingFilter.CLIENT_PREMIUM,
    label: "Client Premium",
    color: brandColor,
    field: "clientPremium",
  },
  {
    id: FundingFilter.LENDER_PREMIUM,
    label: "Lender Premium",
    color: `${brandColor}99`,
    field: "lenderPremium",
  },
  {
    id: FundingFilter.INTEREST_PAYMENT,
    label: "Interest Payment",
    color: `${brandColor}73`,
    field: "clientInterestPayment",
  },
  {
    id: FundingFilter.PLAN_FEE,
    label: "Plan Fee",
    color: `${brandColor}4D`,
    field: "clientPlanFee",
  },
  {
    id: FundingFilter.ORIGINATION_FEE,
    label: "Origination Fee",
    color: `${brandColor}26`,
    field: "clientOriginationFees",
  },
];

const makePeopleFilterOptions = (brandColor: string): FilterOption[] => [
  { id: FundingFilter.LIFE, label: "Life", color: brandColor, field: "clientPremium" },
  {
    id: FundingFilter.DISABILITY,
    label: "Disability",
    color: `${brandColor}99`,
    field: "lenderPremium",
  },
  {
    id: FundingFilter.PLAN_FEE,
    label: "Plan Fees",
    color: `${brandColor}4D`,
    field: "clientPlanFee",
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const accordionHeader = (
  title: string,
  projectionallow?: boolean,
  showProjections?: boolean,
  onToggle?: () => void,
  brandColor: string = PRIMARY,
) => (
  <div className="flex justify-between w-full items-center">
    <div className="text-lg conthrax-font ml-2 text-[#55556D]">{title}</div>
    {projectionallow && (
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <AnimatedCheckbox
          checked={showProjections ?? false}
          onChange={() => onToggle?.()}
          inputId="funding-projections"
          label={
            <span className="flex items-center gap-1">
              <span style={{ width: 39, height: 36, display: "inline-flex", flexShrink: 0 }}>
                <ProjectionsIcon fill={showProjections ? brandColor : "#64748B"} />
              </span>
            </span>
          }
          brandingColor={brandColor}
          size={20}
          hideBorder
        />
      </div>
    )}
  </div>
);

const collapseIcon = (
  <div className="rounded-full bg-[#EBEBEF] p-[1px] cursor-pointer">
    <GoTriangleDown className="text-[#8A8AA3]" size={27} style={{ transition: "transform 0.2s" }} />
  </div>
);
const expandIcon = (
  <div className="rounded-full bg-[#EBEBEF] p-[1px] cursor-pointer">
    <GoTriangleUp
      className="text-[#8A8AA3]"
      size={27}
      style={{ transition: "transform 0.2s", rotate: "140deg" }}
    />
  </div>
);
// ── Main component ────────────────────────────────────────────────────────────

function FundingIntRev() {
  const { isDarkMode } = useContext(ThemeContext);
  const isDark = isDarkMode;
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor } = getThemeColors(branding);
  // const navigate = useNavigate();
  const planId = getSessionStore("planInfo")?.planId;
  const isSplitDollar = getSessionStore("planInfo")?.planType === ProposalTypes.SplitDollar;
  const isBuySell = getSessionStore("planInfo")?.planType === ProposalTypes.BuySell;
  const isKeyPerson = getSessionStore("planInfo")?.planType === ProposalTypes.KeyPersonInsurance;
  // Buy Sell and Key Person share the same "people plan" UI: Life/Disability/Plan
  // Fees filters, the Funding Ledger, and the TrueIQ "Protect" label.
  const isPeoplePlan = isBuySell || isKeyPerson;
  const brandColor = isDark ? secondaryColor : primaryColor;
  const activeFilterOptions = isPeoplePlan
    ? makePeopleFilterOptions(brandColor)
    : makeFilterOptions(brandColor);
  const planType = getSessionStore("planType");

  const getFilterLabel = (id: string, defaultLabel: string) => {
    if (!isSplitDollar) return defaultLabel;
    if (id === "CLIENT_PREMIUM") return "Employee Premium";
    if (id === "LENDER_PREMIUM") return "Employer Premium";
    return defaultLabel;
  };
  const [selectedYear, setSelectedYear] = useState<number>(getSessionStore("ircSelectedYear") ?? 1);
  const [creditType, setCreditType] = useState<string>(
    getSessionStore("ircSelectedCreditType") ?? "preCredit",
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const newYear =
        detail.year !== undefined ? detail.year : getSessionStore("ircSelectedYear") ?? 1;
      const newCreditType =
        detail.creditType !== undefined
          ? detail.creditType
          : getSessionStore("ircSelectedCreditType") ?? "preCredit";
      setSelectedYear(newYear);
      setCreditType(newCreditType);
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  const userAccess = getObjectFromLocalStore("useraccess");
  const isContact = userAccess?.userType === USERTYPE.CONTACT;
  const [insuredPersons, setInsuredPersons] = useState<InsuredPerson[]>([]);

  const apiEndPoint = useMemo(() => {
    switch (planType) {
      case ProposalTypes.SplitDollar:
        return "sdPlanReviewCentre";
      default:
        return "reviewCenterReview";
    }
  }, [planType]);

  // check proposal type
  useEffect(() => {
    if (!planId) return;

    // Buy Sell / Key Person source their roster from the Review Center summary
    // (shareholders / employees) — the same source ReviewCenterOverview uses — so
    // the people, ids and names stay consistent across the Review Center.
    if (isPeoplePlan) {
      makeRequest("interactiveReviewCenterSummary")
        .get(`${planId}?year=${selectedYear}`, true)
        .then((res: any) => {
          const data = res?.data?.data;
          const list: any[] = (isBuySell ? data?.shareholders : data?.employees) ?? [];
          setInsuredPersons(
            list.map((item: any, i: number) => {
              const firstName = item?.user?.profile?.firstName ?? item?.firstName ?? "";
              const lastName = item?.user?.profile?.lastName ?? item?.lastName ?? "";
              return {
                id: item.id ?? item.userId ?? String(i),
                name: `${firstName} ${lastName}`.trim() || item?.name || "Insured",
              };
            }),
          );
        })
        .catch(() => {});
      return;
    }

    // PF / SD: unchanged — roster from the review-center endpoint's insuredList/contacts.
    // QA date override: forward `reviewCenterCurrentDate` (if set) as the `currentDate` query
    // param. Falls back to the plain id when no override is set, so existing behavior is unchanged.
    const reviewCurrentDate = localStorage.getItem("reviewCenterCurrentDate")?.trim();
    const reviewCenterReviewKey = reviewCurrentDate
      ? `${planId}?currentDate=${reviewCurrentDate}`
      : planId;
    makeRequest(apiEndPoint)
      .get(reviewCenterReviewKey, true)
      .then((res: any) => {
        const list: any[] = res?.data?.data?.insuredList ?? res?.data?.data?.contacts ?? [];
        setInsuredPersons(
          list.map((item: any) => {
            const firstName = item?.firstName ?? item?.profile?.firstName ?? "";
            const lastName = item?.lastName ?? item?.profile?.lastName ?? "";

            return {
              id: item.userId ?? item.id,
              name: `${firstName} ${lastName}`.trim() || "Insured",
            };
          }),
        );
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [planId, isPeoplePlan, isBuySell, selectedYear, apiEndPoint]);

  const [selectedInsured, setSelectedInsured] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    new Set(activeFilterOptions.map((f) => f.id)),
  );
  // const [selectedYear] = useState<number>(1);
  const [fundingData, setFundingData] = useState<FundingRow[]>([]);
  const [tableRows, setTableRows] = useState<FundingRow[]>([]);
  // KP/BS funding ledger (lives in the same funding response under `ledger`).
  const [ledger, setLedger] = useState<{
    rows: any[];
    totalBenefit: number;
    totalAnnualizedPremium: number;
  }>({ rows: [], totalBenefit: 0, totalAnnualizedPremium: 0 });
  // Start in the loading state so the whole page shows a loader on first render,
  // not just the funding chart, until the initial funding fetch resolves.
  const [loading, setLoading] = useState(true);
  const [showProjections, setShowProjections] = useState(true);
  const [showIndex, setShowIndex] = useState(false);
  const [selectedChartFilter, setSelectedChartFilter] = useState("year");

  // Seed selected insured once persons load
  useEffect(() => {
    if (insuredPersons.length > 0 && selectedInsured.size === 0) {
      setSelectedInsured(new Set(insuredPersons.map((p) => p.id)));
    }
  }, [insuredPersons, selectedInsured]);

  // When multiple insureds selected, force year filter; when single, revert to age
  useEffect(() => {
    if (selectedInsured.size > 1) {
      setSelectedChartFilter("year");
    } else {
      setSelectedChartFilter("insuredAge");
    }
  }, [selectedInsured]);

  useEffect(() => {
    // if (!planId || !selectedInsured) return;
    setLoading(true);
    const filterIds = JSON.stringify([...activeFilters]);
    const insuredIds = JSON.stringify([...selectedInsured]);
    // creditType (pre/post) only applies to Premium Finance / Split Dollar.
    const creditQuery = planHasCreditType(getSessionStore("planInfo")?.planType)
      ? `&creditType=${creditType}`
      : "";
    makeRequest("interactiveReviewCenterFunding")
      .get(
        `${planId}?filters=${filterIds}&year=${selectedYear}${creditQuery}&insuredIds=${insuredIds}`,
        true,
      )
      .then((res: any) => {
        const graphData: any[] = res?.data?.data?.graphData ?? [];
        setFundingData(graphData.map(isPeoplePlan ? mapKpGraphItemToFundingRow : mapToFundingRow));
        const tableData: any[] = res?.data?.data?.tableData ?? [];
        setTableRows(
          tableData.map(isPeoplePlan ? mapKpTableItemToFundingRow : mapTableItemToFundingRow),
        );
        const apiLedger = res?.data?.data?.ledger;
        setLedger({
          rows: apiLedger?.rows ?? [],
          totalBenefit: apiLedger?.totalBenefit ?? 0,
          totalAnnualizedPremium: apiLedger?.totalAnnualizedPremium ?? 0,
        });
      })
      .catch((err: any) => console.error("Failed to fetch funding data:", err))
      .finally(() => setLoading(false));
  }, [planId, activeFilters, selectedYear, creditType, [...selectedInsured].join(",")]);

  const insuredName = insuredPersons
    .filter((p) => selectedInsured.has(p.id))
    .map((p) => p.name)
    .join(", ");

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const textSub = isDark ? "text-gray-400" : "text-gray-500";
  const toggleFilter = (id: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        // Key Person / Buy Sell must keep at least one funding filter (Life / Disability /
        // Plan Fees) selected — block unchecking the last remaining one.
        if (isPeoplePlan && next.size <= 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Card
      className={`pb-1 border border-1
       border-gray-300 rounded-2xl w-full min-h-screen
         shadow-lg dark:!border-[#3A3A50] dark:bg-[#2A2A3D] `}
      pt={{ body: { className: "p-0" } }}
    >
      <div className="flex flex-col gap-5 pb-8 relative">
        {/* Page-wide loader on the first render — covers the whole page, not just
            the funding chart. Subsequent filter refetches keep the existing UI and
            rely on the chart's own loading indicator. */}
        {loading && fundingData.length === 0 && (
          <div
            className="absolute inset-0 z-20 flex justify-center items-center"
            style={{ backgroundColor: isDark ? "rgba(42,42,61,0.6)" : "rgba(255,255,255,0.6)" }}
          >
            <ProgressSpinner style={{ width: 48, height: 48 }} />
          </div>
        )}
        {/* ── Insured persons + TrueIQ ─────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap px-4 pt-4">
          <div>
            <p
              className={`text-[18px] conthrax-font font-semibold  tracking-wider mb-2 ${textSub}`}
            >
              {isBuySell ? "Shareholders" : isKeyPerson ? "Key Person(s)" : "Insured Person(s)"}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {insuredPersons.length === 0 ? (
                <p className="text-sm text-gray-400">
                  {isBuySell
                    ? "No shareholders"
                    : isKeyPerson
                    ? "No key persons"
                    : "No person is insured"}
                </p>
              ) : (
                insuredPersons.map((person) => (
                  <AnimatedCheckbox
                    key={person.id}
                    checked={selectedInsured.has(person.id)}
                    onChange={
                      () =>
                        setSelectedInsured((prev) => {
                          const next = new Set(prev);
                          if (next.has(person.id)) {
                            if (next.size > 1) next.delete(person.id);
                          } else {
                            next.add(person.id);
                          }
                          return next;
                        })
                      // eslint-disable-next-line
                    }
                    inputId={person.id}
                    label={person.name}
                    brandingColor={brandColor}
                    size={20}
                    borderExtra={`1px solid ${brandColor}`}
                  />
                ))
              )}
            </div>
          </div>

          {/* TrueIQ badge */}
          {!isContact && (
            <div
              className="flex flex-col rounded-md items-center gap-1.5 px-5 py-2"
              style={{
                border: `1px solid ${brandColor}55`,
                color: isDark ? secondaryColor : brandColor,
                background: isDark ? `${brandColor}15` : `${brandColor}08`,
              }}
            >
              <div className="flex gap-1 items-center">
                <TrueIqIcon width={18} fill="#55556D" />
                <span className="text-md conthrax-font text-[#55556D]">TrueIQ</span>
              </div>

              <AnimatedCheckbox
                checked={showIndex}
                onChange={() => setShowIndex((v) => !v)}
                inputId="trueiq-index"
                label={isPeoplePlan ? "Protect" : "Index"}
                brandingColor={brandColor}
                size={20}
                hideBorder
              />
            </div>
          )}
        </div>
        {/* ── Funding filter checkboxes ────────────────────────────────────── */}
        <div className="px-4">
          <p className={`text-[18px] font-semibold  conthrax-font tracking-wider mb-2 ${textSub}`}>
            Funding
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {activeFilterOptions.map((f) => (
              <AnimatedCheckbox
                key={f.id}
                checked={activeFilters.has(f.id)}
                onChange={() => toggleFilter(f.id)}
                inputId={f.id}
                label={
                  <span className="flex items-center gap-2">
                    {getFilterLabel(f.id, f.label)}

                    <span
                      className="w-[12px] h-[12px] rounded-full inline-block"
                      style={{ backgroundColor: f.color }}
                    />
                  </span>
                }
                brandingColor={brandColor}
                size={20}
                borderExtra={`1px solid ${brandColor}`}
              />
            ))}
          </div>
        </div>

        <Accordion
          collapseIcon={collapseIcon}
          expandIcon={expandIcon}
          multiple
          activeIndex={[0, 1, 2]}
          className="bg-gray-100 dark:!bg-[#111111] rounded-md ip-project-accordion-main"
        >
          {isPeoplePlan && (
            <AccordionTab
              headerTemplate={accordionHeader("Funding Ledger")}
              headerClassName="interactive-proposal-projection"
              className="bg-light-primary dark:bg-[#2A2A3D] rounded-md border-none"
              contentClassName="interactive-proposal-projection-content"
            >
              <FundingLedger
                rows={ledger.rows}
                totalBenefit={ledger.totalBenefit}
                totalAnnualizedPremium={ledger.totalAnnualizedPremium}
                isDark={isDark}
              />
            </AccordionTab>
          )}

          <AccordionTab
            headerTemplate={accordionHeader(
              "Funding Chart",
              true,
              showProjections,
              () => setShowProjections((v) => !v),
              brandColor,
            )}
            headerClassName="interactive-proposal-projection"
            contentClassName="interactive-proposal-projection-content"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
          >
            <div className={`overflow-hidden ${cardBg}`}>
              <div className="px-4 pt-3 pb-2">
                {!loading && fundingData.length === 0 ? (
                  <div
                    className="flex justify-center items-center"
                    style={{
                      backgroundColor: isDark ? "#000000" : "#ffffff",
                      borderRadius: "8px",
                      padding: "5px",
                      height: "300px",
                    }}
                  >
                    <div className="text-center">
                      <i className="pi pi-chart-line text-3xl text-gray-400 mb-2" />
                      <p className={`text-lg ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                        No data available
                      </p>
                    </div>
                  </div>
                ) : (
                  <FundingChart
                    data={fundingData}
                    activeFilters={activeFilters}
                    isDark={isDark}
                    selectedYear={selectedYear}
                    loading={loading}
                    showProjections={showProjections}
                    // onShowProjectionsChange={setShowProjections}
                    isContact={isContact}
                    showIndex={showIndex}
                    selectedChartFilter={selectedChartFilter}
                    isSplitDollar={isSplitDollar}
                    isPeoplePlan={isPeoplePlan}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                  />
                )}
              </div>

              <div className="mt-1 px-1 pb-2">
                <AssetValueChartFilter
                  selectedChartFilter={selectedChartFilter}
                  onChartFilterChange={setSelectedChartFilter}
                  showProjections={showProjections}
                  onProjectionsToggle={() => setShowProjections((v) => !v)}
                  enableAgeFilter={selectedInsured.size <= 1}
                  overrideCalendarIcon
                  hideProjection
                />
              </div>
              {insuredName && (
                <div className="flex items-center px-4 pb-3 gap-2">
                  <p className={`text-[11px] text-center flex-1 ${textSub}`}>
                    Plan Funding for {insuredName}
                  </p>
                </div>
              )}
            </div>
          </AccordionTab>
          <AccordionTab
            headerTemplate={accordionHeader("Funding Table")}
            contentClassName="interactive-proposal-projection-content"
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md "
          >
            <FundingTable
              data={tableRows}
              selectedYear={selectedYear}
              insuredName={insuredName}
              isDark={isDark}
              cardBg={cardBg}
              // divider={divider}
              // textMain={textMain}
              showProjections={showProjections}
              textSub={textSub}
              activeFilters={activeFilters}
              isSplitDollar={isSplitDollar}
              isPeoplePlan={isPeoplePlan}
              hoverColor={brandColor}
            />
          </AccordionTab>
        </Accordion>
      </div>
    </Card>
  );
}

export default FundingIntRev;
